import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Info, X, Plus, Infinity } from "lucide-react";
import {
  getSubscriptionSettings,
  putSubscriptionSettings,
  listSubscriptionPlans,
} from "../../../../Services/adminCorporates";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Switch } from "../../../../ui/switch";
import { Skeleton } from "../../../../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../ui/select";

// Per-spec suggested module codes; the field is freeform on submit so admins
// can add codes that aren't in this list yet.
const SUGGESTED_MODULES = [
  "COACHING",
  "FAITH",
  "SETTINGS",
  "ELEMENTS",
  "ASSESSMENT",
  "CHAT",
  "CONNECTIONS",
  "DIALOGS_FOR_TWO",
  "RELATIONSHIPS",
];

const UNLIMITED = -1;

const NumberLimitField = ({ id, label, value, onChange, hint }) => {
  const isUnlimited = value === UNLIMITED;
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <Input
          id={id}
          type="number"
          min={0}
          disabled={isUnlimited}
          value={isUnlimited ? "" : value ?? ""}
          onChange={(e) => {
            const n = e.target.value;
            onChange(n === "" ? null : Math.max(0, Number(n)));
          }}
          placeholder={isUnlimited ? "Unlimited" : "0"}
          className="max-w-[180px]"
        />
        <label className="inline-flex items-center gap-2 text-sm">
          <Switch
            checked={isUnlimited}
            onCheckedChange={(checked) => onChange(checked ? UNLIMITED : null)}
            aria-label={`Toggle unlimited ${label}`}
          />
          <span className="inline-flex items-center gap-1">
            <Infinity className="h-4 w-4" />
            Unlimited
          </span>
        </label>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

const Subscription = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedNotice, setSavedNotice] = useState(null);

  const [planTemplateId, setPlanTemplateId] = useState("");
  const [modules, setModules] = useState([]);
  const [moduleDraft, setModuleDraft] = useState("");
  const [voiceMinutes, setVoiceMinutes] = useState(null);
  const [textMessages, setTextMessages] = useState(null);
  const [extraJson, setExtraJson] = useState("");

  const [plans, setPlans] = useState([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [settings, planList] = await Promise.all([
        getSubscriptionSettings(id),
        listSubscriptionPlans().catch(() => []),
      ]);
      setPlanTemplateId(settings?.subscription_plan_template_id || "");
      setModules(settings?.modules_enabled || []);
      const overrides = settings?.custom_overrides || {};
      setVoiceMinutes(
        Object.prototype.hasOwnProperty.call(
          overrides,
          "voice_coaching_minutes_per_month"
        )
          ? overrides.voice_coaching_minutes_per_month
          : null
      );
      setTextMessages(
        Object.prototype.hasOwnProperty.call(
          overrides,
          "text_coaching_messages_per_month"
        )
          ? overrides.text_coaching_messages_per_month
          : null
      );
      setExtraJson(
        overrides.extra && Object.keys(overrides.extra).length
          ? JSON.stringify(overrides.extra, null, 2)
          : ""
      );
      setPlans(Array.isArray(planList) ? planList : []);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load settings"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const addModule = () => {
    const trimmed = moduleDraft.trim().toUpperCase();
    if (!trimmed || modules.includes(trimmed)) {
      setModuleDraft("");
      return;
    }
    setModules([...modules, trimmed]);
    setModuleDraft("");
  };

  const removeModule = (mod) => {
    setModules(modules.filter((m) => m !== mod));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSavedNotice(null);

    let extraParsed = {};
    if (extraJson.trim()) {
      try {
        extraParsed = JSON.parse(extraJson);
      } catch (_) {
        setError("Extra overrides must be valid JSON.");
        setSaving(false);
        return;
      }
    }

    const customOverrides = { extra: extraParsed };
    if (voiceMinutes !== null && voiceMinutes !== undefined) {
      customOverrides.voice_coaching_minutes_per_month = voiceMinutes;
    }
    if (textMessages !== null && textMessages !== undefined) {
      customOverrides.text_coaching_messages_per_month = textMessages;
    }

    try {
      await putSubscriptionSettings(id, {
        subscription_plan_template_id: planTemplateId || undefined,
        modules_enabled: modules,
        custom_overrides: customOverrides,
      });
      setSavedNotice("Subscription settings saved.");
      load();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <Info className="h-4 w-4 mt-0.5" />
        <p className="m-0">
          Changes apply only to <strong>new</strong> signups. Existing active
          members keep their currently-granted access until manual
          re-provisioning.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Plan template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {plans.length > 0 ? (
            <Select value={planTemplateId} onValueChange={setPlanTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a plan template…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">— None —</SelectItem>
                {plans.map((plan) => (
                  <SelectItem key={plan.id || plan._id} value={plan.id || plan._id}>
                    {plan.name}
                    {plan.billing_cycle ? ` · ${plan.billing_cycle}` : ""}
                    {plan.price ? ` · $${plan.price}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={planTemplateId}
              onChange={(e) => setPlanTemplateId(e.target.value)}
              placeholder="Plan template id (no plans loaded)"
            />
          )}
          <p className="text-xs text-muted-foreground">
            Storing this is documentation only — actual access is determined by
            the modules and custom overrides below.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modules enabled</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {modules.length === 0 && (
              <span className="text-sm text-muted-foreground italic">
                No modules selected.
              </span>
            )}
            {modules.map((mod) => (
              <span
                key={mod}
                className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs font-mono"
              >
                {mod}
                <button
                  type="button"
                  onClick={() => removeModule(mod)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${mod}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={moduleDraft}
              onChange={(e) => setModuleDraft(e.target.value)}
              placeholder="MODULE_CODE"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addModule();
                }
              }}
            />
            <Button type="button" variant="outline" onClick={addModule}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {SUGGESTED_MODULES.filter((m) => !modules.includes(m)).map((mod) => (
              <button
                key={mod}
                type="button"
                onClick={() => setModules([...modules, mod])}
                className="text-xs rounded-full border border-dashed px-3 py-1 text-muted-foreground hover:text-foreground hover:border-solid"
              >
                + {mod}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom overrides</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <NumberLimitField
            id="voice_minutes"
            label="Voice coaching minutes / month"
            value={voiceMinutes}
            onChange={setVoiceMinutes}
            hint="Leave blank to inherit the plan template default."
          />
          <NumberLimitField
            id="text_messages"
            label="Text coaching messages / month"
            value={textMessages}
            onChange={setTextMessages}
            hint="Leave blank to inherit the plan template default."
          />
          <div className="grid gap-1.5">
            <Label htmlFor="extra-json">Extra overrides (JSON)</Label>
            <textarea
              id="extra-json"
              rows={5}
              value={extraJson}
              onChange={(e) => setExtraJson(e.target.value)}
              placeholder='{"feature_x": true}'
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <p className="text-xs text-muted-foreground">
              Optional. Free-form JSON object for override flags not covered above.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {savedNotice && (
          <span className="text-sm text-emerald-600">{savedNotice}</span>
        )}
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default Subscription;
